<template>
  <main class="bg-chatbot">
    <div class="page-space-25"></div>
    <section class="container-lil">
      
      <!-- Widget Chatbot -->
      <div class="flex flex-col align-items-center mb-10">
        <div class="widget-chatbot h-auto flex flex-col align-items-center px-5 py-6 mb-7">
          <h1 class="secondary-font flex-center gap-4 my-2">
            <img src="/public/icons/icon-aide-tchat.svg" class="size-base mt--2" alt="" /> Live Tchat
          </h1>
          <h4 class="secondary-font text-center my-5">
            Discuter avec un conseiller client
          </h4>

          <div class="flex-center mb-5">
            <img 
              v-for="(avatar, index) in chatAvatars" 
              :key="index"
              :src="avatar" 
              class="size-2xl rounded-100 mx-2 my-2" 
              alt="Conseiller client"
            />
          </div>
          
          <button 
            class="btn lg icon-right btn-chatbot flex-center px-10 gap-2"
            @click="openLiveChat"
          >
            <span>Démarrer &nbsp;</span>
            <img src="/public/icons/icon-send.svg" class="btn-icon" alt="" />
          </button>
        </div>
        
        <button 
          class="btn lg btn-chatbot flex-center px-10 gap-2 mb-5"
          @click="openWhatsApp"
        >
          <img src="/public/icons/icon-aide-whatsapp.svg" class="btn-icon" alt="" />
          <span>Discuter sur WhatsApp</span>
        </button>
        
        <div class="clear-both h-20"></div>
        
        <button 
          class="btn lg btn-chatbot flex-center px-10 gap-2"
          @click="makePhoneCall"
        >
          <img src="/public/icons/icon-aide-appel.svg" class="btn-icon" alt="" />
          <span>Service client</span>
        </button>
      </div>
      

      <!-- Section FAQ -->
      <div class="mb-10">
        <h1 class="secondary-font flex-center gap-4 my-2">
          <img src="/public/icons/icon-aide-faq.svg" class="size-base mt--2" alt="" /> FAQ
        </h1>

        <template v-if="loadingFaqs">
          <p class="text-center my-5">Chargement des questions...</p>
        </template>
        
        <template v-else>
            <div
              v-for="(faq, faqIndex) in faqs"
              :key="faq.id || faqIndex"
              class="faq-wrapper"
            >
              <div 
                class="btn-faq"
                :class="{ 'active': expandedFaqs[faq.id || faqIndex] }"
                @click="toggleFaq(faq.id || faqIndex)"
              >
                {{ faq.question || faq.libelle }}
                <img 
                  src="/public/icons/icon-arrow-faq.svg" 
                  class="size-xs icon-black faq-icon" 
                  alt="" 
                />
              </div>
              <p 
                class="faq-content"
                :class="{ 'active': expandedFaqs[faq.id || faqIndex] }"
                v-html="faq.reponse || faq.contenu_aide"
              ></p>
            </div>

          <div v-if="faqs.length === 0" class="text-center my-5">
            <p>Aucune FAQ disponible pour le moment.</p>
          </div>
        </template>

        <div class="clear-both h-30"></div>
        <router-link to="/front/aide/faq">
          <h5 class="flex-center gap-4 text-black">
            Voir toutes les questions 
            <img src="/public/icons/icon-go.svg" class="size-medium" alt="" />
          </h5>
        </router-link>
      </div>

      <div class="clear-both h-10"></div>

      <!-- Section Tutoriels -->
      <div>
        <h1 class="secondary-font flex-center gap-4 my-2">
          <img src="/public/icons/icon-aide-tutoriel.svg" class="size-base mt--2" alt="" /> Tutoriels
        </h1>

        <template v-if="loadingTutoriels">
          <p class="text-center my-5">Chargement des tutoriels...</p>
        </template>
        
        <template v-else>
          <div class="grid-web3 mt-5">
            <router-link
              v-for="tutoriel in tutoriels.slice(0, 3)"
              :key="tutoriel.id"
              :to="`/front/aide/tutoriels/${tutoriel.slug || tutoriel.id}`"
            >
              <div class="card px-2 py-2">
                <img 
                  :src="getTutorielImage(tutoriel)" 
                  width="100%" 
                  class="rounded-15" 
                  alt=""
                />
                <h5 class="font-semibold text-black mt-3 mb-4 px-4">
                  {{ tutoriel.libelle || tutoriel.titre }}
                </h5>
              </div>
            </router-link>
          </div>

          <div v-if="tutoriels.length === 0" class="text-center my-5">
            <p>Aucun tutoriel disponible pour le moment.</p>
          </div>
        </template>

        <div class="clear-both h-30"></div>
        <router-link to="/front/aide/tutoriels">
          <h5 class="flex-center gap-4 text-black">
            Voir tous les tutoriels 
            <img src="/public/icons/icon-go.svg" class="size-medium" alt="" />
          </h5>
        </router-link>
      </div>

      <div class="clear-both h-70"></div>
    </section>
  </main>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import communicationService from '@/services/communicationService'
import contentService from '@/services/contentService'

export default defineComponent({
  name: 'FrontofficeAide',
  setup() {
    const router = useRouter()
    const config = communicationService.getConfig()
    
    // Avatars pour le chatbot (images par défaut)
    const chatAvatars = ref([
      '/public/assets/tchat/600w/avatar.jpg',
      '/public/assets/tchat/600w/avatar2.jpg',
      '/public/assets/tchat/600w/avatar3.jpg',
      '/public/assets/tchat/600w/avatar4.jpg',
      '/public/assets/tchat/600w/avatar5.jpg',
      '/public/assets/tchat/600w/avatar6.jpg'
    ])

    // FAQs
    const faqs = ref<any[]>([])
    const expandedFaqs = reactive<Record<number | string, boolean>>({})
    const loadingFaqs = ref(false)

    // Tutoriels
    const tutoriels = ref<any[]>([])
    const loadingTutoriels = ref(false)

    /**
     * Toggle FAQ accordion
     */
    const toggleFaq = (faqId: number | string) => {
      expandedFaqs[faqId] = !expandedFaqs[faqId]
    }

    /**
     * Charger les FAQs
     */
    const loadFaqs = async () => {
      loadingFaqs.value = true
      try {
        const response = await contentService.getFaqs()
        
        if (response.data) {
          // Si les FAQs sont groupées par catégorie
          if (Array.isArray(response.data)) {
            faqs.value = response.data
          } else {
            // Si c'est un objet avec des catégories
            faqs.value = Object.values(response.data)
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des FAQs:', error)
      } finally {
        loadingFaqs.value = false
      }
    }

    /**
     * Charger les tutoriels
     */
    const loadTutoriels = async () => {
      loadingTutoriels.value = true
      try {
        const response = await contentService.getTutoriels()
        console.log(response.data);
        if (response.data) {
          if (Array.isArray(response.data)) {
            tutoriels.value = response.data
          } else {
            // Si c'est un objet paginé avec une propriété data
            const data = response.data as any
            tutoriels.value = data.data || []
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des tutoriels:', error)
      } finally {
        loadingTutoriels.value = false
      }
    }

    /**
     * Obtenir l'image d'un tutoriel
     */
    const getTutorielImage = (tutoriel: any) => {
      return '/public/assets/aide/1000w/pix-aide-tutoriel2.jpg';
      /*if (tutoriel.fichier_photo) {
        // Construire l'URL complète de l'image
        const baseUrl = process.env.VUE_APP_API_URL?.replace('/api', '') || 'http://localhost:8000'
        return `${baseUrl}/storage/tutoriel/${tutoriel.fichier_photo}`
      }
      return '/public/assets/aide/1000w/pix-aide-tutoriel2.jpg' */
    }

    /**
     * Ouvrir la page Service Client
     */
    const makePhoneCall = () => {
      router.push('/front/aide/service-client')
    }

    /**
     * Ouvrir le live chat
     */
    const openLiveChat = () => {
      router.push('/front/aide/live-tchats')
    }

    /**
     * Ouvrir WhatsApp
     */
    const openWhatsApp = () => {
      communicationService.openWhatsApp()
    }

    // Charger les données au montage
    onMounted(() => {
      loadFaqs()
      loadTutoriels()
    })

    return {
      chatAvatars,
      faqs,
      expandedFaqs,
      loadingFaqs,
      tutoriels,
      loadingTutoriels,
      toggleFaq,
      getTutorielImage,
      makePhoneCall,
      openLiveChat,
      openWhatsApp
    }
  }
})
</script>

<style scoped>
/* Les styles globaux sont déjà définis dans les fichiers CSS globaux */
/* On garde seulement les styles spécifiques si nécessaire */
.faq-wrapper {
  width: 100%;
}
</style>

